// AD-8 · Professional Passport · Public View — public read-only page (r2).
// No auth. Renders exactly the payload returned by
// GET /api/passport/professional/public/{public_slug}.
// - Locked page title, current-practice label, empty-state line,
//   footer copy (mirrored from backend registry).
// - Alphabetical statement order preserved from the backend.
// - Non-revealing 404 → generic "Not found" state.

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AD8_PAGE_TITLE,
  AD8_CURRENT_PRACTICE_LABEL,
  AD8_EMPTY_STATE_LINE,
  AD8_FOOTER_LINE,
} from "../lib/ad8Copy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PassportProfessionalPublic() {
  const { publicSlug } = useParams();
  const [state, setState] = useState({ loading: true, notFound: false, payload: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/passport/professional/public/${encodeURIComponent(publicSlug || "")}`,
        );
        if (cancelled) return;
        setState({ loading: false, notFound: false, payload: res.data });
      } catch {
        if (cancelled) return;
        setState({ loading: false, notFound: true, payload: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicSlug]);

  if (state.loading) {
    return (
      <div
        data-testid="ad8-public-loading"
        className="min-h-screen bg-slate-50 text-slate-900"
      />
    );
  }

  if (state.notFound) {
    return (
      <div
        data-testid="ad8-public-not-found"
        className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4"
      >
        <p className="text-slate-500 text-sm">Not found.</p>
      </div>
    );
  }

  const { display_name, statements } = state.payload || {};
  return (
    <div
      data-testid="ad8-public-passport"
      className="min-h-screen bg-slate-50 text-slate-900"
    >
      <div className="max-w-xl mx-auto px-4 py-12 space-y-8">
        {display_name ? (
          <header className="space-y-2">
            <h1
              data-testid="ad8-public-display-name"
              className="text-3xl font-semibold leading-tight"
            >
              {display_name}
            </h1>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              {AD8_PAGE_TITLE}
            </p>
          </header>
        ) : (
          <header>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              {AD8_PAGE_TITLE}
            </p>
          </header>
        )}

        <section className="space-y-4" data-testid="ad8-public-current-practice">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest">
            {AD8_CURRENT_PRACTICE_LABEL}
          </h2>
          {statements && statements.length > 0 ? (
            <ul className="space-y-4 pl-4 list-disc marker:text-slate-300">
              {statements.map((s) => (
                <li
                  key={s.id}
                  data-testid={`ad8-public-statement-${s.id}`}
                  className="text-lg leading-relaxed"
                >
                  {s.wording}
                </li>
              ))}
            </ul>
          ) : (
            <p
              data-testid="ad8-public-empty-state"
              className="text-lg leading-relaxed text-slate-500"
            >
              {AD8_EMPTY_STATE_LINE}
            </p>
          )}
        </section>

        <footer
          data-testid="ad8-public-footer"
          className="text-xs text-slate-500 leading-relaxed pt-6 border-t border-slate-200"
        >
          {AD8_FOOTER_LINE}
        </footer>
      </div>
    </div>
  );
}
